import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { isLowTextContrast, themeTextContrast } from 'fym-shared/src/music24/kitPalette';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { ColourSwatchField } from './ColourSwatchField';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeColoursRowProps {
  themeId: ReleaseThemeId;
  /** The five colours to show: the draft, else the saved colours. */
  palette: readonly string[];
  /** The colours are the artist's own, not the cover colours. "Reset to cover colours" shows. */
  isOwn: boolean;
  disabled: boolean;
  /** The whole new palette, with one colour changed. */
  onChange: (palette: string[]) => void;
  onReset: () => void;
}

/**
 * "Colours" (owner request 2026-10-06, in the style of the font row of design q2): the five colours
 * that the theme builds its colours from. They are the colours of the cover until the artist
 * changes one; each one has a picker and a hex field. A change goes into the draft of the Theme
 * panel (live preview, Save and Cancel). "Reset to cover colours" puts the auto colours back. A
 * line warns when the text of the theme has less than 4.5:1 contrast on its background; it never
 * blocks Save.
 */
const ThemeColoursRowComponent: React.FC<ThemeColoursRowProps> = ({
  themeId,
  palette,
  isOwn,
  disabled,
  onChange,
  onReset,
}) => {
  const intl = useIntl();
  const ratio = useMemo(() => themeTextContrast(themeId, palette), [palette, themeId]);
  const isLow = isLowTextContrast(ratio);

  const commit = useCallback(
    (index: number, colour: string) => {
      onChange(palette.map((current, place) => (place === index ? colour : current)));
    },
    [onChange, palette]
  );

  return (
    <div className={styles.row} data-testid="theme-colours-row">
      <div className={styles.grow}>
        <b className={styles.rowTitle}>{intl.formatMessage(themeMessages.coloursTitle)}</b>
        <small className={styles.rowHint}>
          {intl.formatMessage(isOwn ? themeMessages.coloursOwn : themeMessages.coloursCover)}
        </small>
        <div className={styles.swatches} role="group" aria-label={intl.formatMessage(themeMessages.coloursTitle)}>
          {palette.map((colour, index) => (
            // The five places are fixed, and two places can hold the same colour.
            // eslint-disable-next-line react/no-array-index-key
            <ColourSwatchField key={index} index={index} value={colour} disabled={disabled} onCommit={commit} />
          ))}
        </div>
        {isLow && (
          <small className={styles.colourWarning} role="status" data-testid="theme-colours-warning">
            {intl.formatMessage(themeMessages.coloursLowContrast, {
              theme: intl.formatMessage(releaseThemeMessages[themeId].name),
              ratio: ratio.toFixed(1),
            })}
          </small>
        )}
      </div>
      {isOwn && (
        <button type="button" className={styles.linkButton} disabled={disabled} onClick={onReset}>
          {intl.formatMessage(themeMessages.coloursReset)}
        </button>
      )}
    </div>
  );
};

export const ThemeColoursRow = TypedMemo(ThemeColoursRowComponent);
